import { ChangeEvent, useState } from 'react';
import { useCookies } from 'react-cookie';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RemoveIcon from '@mui/icons-material/Remove';
import { MuiGrid } from '../../../../../../../modules/material';
import { updateSitePricingPackage } from '../../../../../store/features/packages';
import { PricingPackageSelection } from '../../../../../store/features/packages/types';
import {
  useRootDispatch,
  useRootSelector,
} from '../../../../../../../store/hooks';
import { useCustomSeatLimit } from '../../../../../hooks/basket/useCustomSeatLimit';
import { selectSitePricingProductsData } from '../../../../../store/features/products';

import { StyledNumberInputField, StyledIconButton } from './styles';
import { pushAddToCartForGTM } from '../../../../../helpers/shared/pushAddToCartForGTM';

export interface NumbersBarProps {
  quantity: number;
  packageId: number;
  packageSelection: PricingPackageSelection[];
}

const maxPackagesLimit = 99;
const minPackagesLimit = 1;

export default function NumbersBar(props: NumbersBarProps) {
  const { quantity, packageId, packageSelection } = props;
  const [cookies] = useCookies(['basket_id']);
  const { basket_id } = cookies;
  const dispatch = useRootDispatch();
  const { limit, limitReached } = useCustomSeatLimit();
  const pricingProductsData = useRootSelector(selectSitePricingProductsData);

  // The packageNumber is using to show correct count when
  // user changed the count but the new value is still not saved on server
  const [packageNumber, setPackageNumber] = useState<number | null>(null);
  const [isRequestInProgress, setIsRequestInProgress] =
    useState<boolean>(false);

  const effectiveMaxPackagesLimit = limit > 0 ? limit : maxPackagesLimit;

  const updatePackageNumber = (newValue: number) => {
    setIsRequestInProgress(true);

    dispatch(
      updateSitePricingPackage({
        packageId,
        basketId: basket_id,
        quantity: newValue,
      }),
    ).then(({ meta: { requestStatus } }) => {
      const addedQuantity = newValue - quantity;

      if (requestStatus === 'fulfilled' && addedQuantity > 0) {
        pushAddToCartForGTM(
          pricingProductsData,
          packageSelection.map(({ productId }) => productId),
          addedQuantity,
        );
      }

      setPackageNumber(null);
      setIsRequestInProgress(false);
    });
  };

  const handleNumberChange = (newValue: number) => {
    const currentNumber = packageNumber || quantity;
    return updatePackageNumber(currentNumber + newValue);
  };

  const onBlurHandler = () => {
    const isValidNumber =
      packageNumber &&
      !isNaN(packageNumber) &&
      packageNumber <= effectiveMaxPackagesLimit &&
      packageNumber !== quantity;

    if (isValidNumber) {
      updatePackageNumber(packageNumber);
    }
  };

  const onChangeInputHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const newQuantity = parseInt(event.target.value);
    const isValidValue =
      !isNaN(newQuantity) &&
      newQuantity >= minPackagesLimit &&
      newQuantity <= effectiveMaxPackagesLimit;

    if (isValidValue) {
      setPackageNumber(newQuantity);
    }
  };

  return (
    <MuiGrid
      spacing={0.5}
      container
      alignItems="start"
      justifyContent="start"
      sx={{ flexWrap: 'nowrap' }}
      className="informa-payment-numbers-bar"
    >
      <MuiGrid item>
        <StyledIconButton
          size="small"
          disabled={isRequestInProgress}
          onClick={() => handleNumberChange(-1)}
        >
          {quantity > 1 ? (
            <RemoveIcon fontSize="small" />
          ) : (
            <DeleteIcon fontSize="small" />
          )}
        </StyledIconButton>
      </MuiGrid>
      <MuiGrid item>
        <StyledNumberInputField
          type="number"
          value={packageNumber || quantity}
          onChange={onChangeInputHandler}
          disabled={isRequestInProgress || limitReached}
          fullWidth={false}
          sx={{ borderRadius: '2px' }}
          inputProps={{
            min: minPackagesLimit,
            max: effectiveMaxPackagesLimit,
            onBlur: onBlurHandler,
          }}
        />
      </MuiGrid>
      <MuiGrid item>
        <StyledIconButton
          size="small"
          disabled={
            quantity >= effectiveMaxPackagesLimit || isRequestInProgress
          }
          onClick={() => handleNumberChange(1)}
        >
          <AddIcon fontSize="small" />
        </StyledIconButton>
      </MuiGrid>
    </MuiGrid>
  );
}
